import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import cloneDeep from 'lodash/cloneDeep';
import {
  Leistung,
  LeistungEigenschaft,
  Produktlinie,
  selectIndicesOfDeactivatedProduktlinien,
  UebergreifendItem,
  ZusatzoptionItem,
} from '@ps20-frontend/frontend-models';
import { convertDate } from '@ps20-frontend/utils';
import { Feature, Produktlinienname, TaasTarifrisikoschluesselURI } from '@ps20/bff-models';
import { InvleistungDialogData } from '../../app/angebot/invleistung-dialog/models/invleistung-dialog-data.model';
import { IAppState } from '../appState';
import { UnfFachschluessel, UnfallProduktlinienname, Berufsrisikogruppe } from '../model/unf.enums';
import { kus5theSaeuleVisible, selectSelectedProduktName } from './kus-absprung.selector';

export const selectProduktlinie = (
  state: IAppState,
  produktname: Produktlinienname,
): Produktlinie => {
  return state.produkt.produktlinien.find(linie => linie.produktName === produktname);
};

export const selectSelectedProduktlinie = (state: IAppState): Produktlinie =>
  getSelectedProduktlinie(state.produkt);

export const selectProduktlinien = (state: IAppState): Array<Produktlinie> =>
  state.produkt?.produktlinien || [];

export const selectProduktlinenWithKus = (state: IAppState): Array<Produktlinie> => {
  const linien = cloneDeep(state.produkt?.produktlinien) || [];

  if (!kus5theSaeuleVisible(state)) {
    return linien;
  }

  const selectSelectedProduktlinie = getSelectedProduktlinie(state.produkt);

  const kusLinie: Produktlinie = {
    produktName: UnfallProduktlinienname.KUS_5th_SAEULE as unknown as Produktlinienname,
    zusatzoptionen: cloneDeep(selectSelectedProduktlinie.zusatzoptionen),
    leistungen: cloneDeep(selectSelectedProduktlinie.leistungen),
    deactivated: false,
    selected: false,
    gefahren: [],
    langtext: 'LANGTEXT_KUS',
    kurztext: 'Kurztext KUS',
    rbdMax: 0,
    preisverzeichnis: 0,
    title: '',
  };

  return [...linien, kusLinie];
};

export const selectUebergreifendeLeistungen = (state: IAppState): Array<UebergreifendItem> =>
  state.produkt?.uebergreifend || [];

export const selectSelectedProduktlinieIndex = (state: IAppState): number => {
  return state.produkt?.produktlinien.findIndex(produktlinie => produktlinie.selected);
};

export const selectSelectedZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItem> => {
  const zusatzoptionen = getSelectedProduktlinie(state.produkt)?.zusatzoptionen || [];
  return zusatzoptionen.filter(elem => elem.isSelected);
};

export const selectLeistungOfSelectedProdukt = (
  state: IAppState,
  fachschluessel: string,
): Leistung | undefined => {
  const leistungen = getSelectedProduktlinie(state.produkt)?.leistungen || [];
  return leistungen.find(option => option.fachschluessel === fachschluessel);
};

export const selectSelectedProductZusatzoptionItems = (state: IAppState): ZusatzoptionItem[] => {
  return getSelectedProduktlinie(state.produkt)?.zusatzoptionen || [];
};

export const selectSelectedProductZusatzoptionItemsProfiSport = (
  state: IAppState,
): ZusatzoptionItem[] => {
  const zusatzoptionItems = getSelectedProduktlinie(state.produkt)?.zusatzoptionen || [];
  if (state.vp && state.vp?.berufsrisikogruppe === Berufsrisikogruppe.PROFISPORTLER) {
    return zusatzoptionItems.filter(
      zusatzoption => zusatzoption.name !== TaasTarifrisikoschluesselURI.AKUTLEISTUNG,
    );
  }
  return zusatzoptionItems;
};

export const hasLeistungSelected = (state: IAppState, fachschluessel: string): boolean => {
  const leistung = selectLeistungOfSelectedProdukt(state, fachschluessel);
  return Boolean(leistung) && leistung.eigenschaft !== LeistungEigenschaft.EXKLUSIVE;
};

export const hasZusatzoptionSelected = (state: IAppState, fachschluessel: string): boolean => {
  const produktlinie = getSelectedProduktlinie(state.produkt);
  const zusatzoptionItems = produktlinie?.zusatzoptionen || [];
  return zusatzoptionItems.some(item => item.fachschluessel === fachschluessel && item.isSelected);
};

export function isDateInRange(
  gebDatum: string,
  vertragsBeginn: string,
  minYears: number,
  maxYears?: number,
): boolean {
  gebDatum = gebDatum.includes('.') ? convertDate(gebDatum) : gebDatum;
  vertragsBeginn = vertragsBeginn.includes('.') ? convertDate(vertragsBeginn) : vertragsBeginn;
  const ageDifMs = new Date(vertragsBeginn).getTime() - new Date(gebDatum).getTime();
  const ageDate = new Date(ageDifMs);
  const result = Math.abs(ageDate.getUTCFullYear() - 1970);
  if (maxYears) {
    return result >= minYears && result < maxYears;
  }
  return result >= minYears;
}

export const selectInvleistungGrafikDataForAuspreagung = (
  state: IAppState,
  produktName: Produktlinienname,
): InvleistungDialogData => {
  let produktlinie: Produktlinie;
  if (produktName === UnfallProduktlinienname.KUS_5th_SAEULE) {
    produktlinie = selectSelectedProduktlinie(state);
  } else {
    produktlinie = selectProduktlinie(state, produktName);
  }

  const topschutzLeistung = produktlinie?.leistungen?.find(
    leistung => leistung.fachschluessel === UnfFachschluessel.TOPSCHUTZ,
  );
  const invSummeLeistung = produktlinie?.leistungen?.find(
    leistung => leistung.fachschluessel === UnfFachschluessel.INV_SUMME,
  );
  const leistungAb = produktlinie?.leistungen?.find(
    leistung => leistung.fachschluessel === UnfFachschluessel.LEISTUNG_AB,
  );

  return {
    isTopschutz: topschutzLeistung?.eigenschaft == LeistungEigenschaft.INKLUSIVE,
    topschutzId: topschutzLeistung?.id,
    invaliditaetssumme: invSummeLeistung?.eigenschaft,
    leistungAb: Number(leistungAb?.eigenschaft),
    produktname: produktlinie.produktName,
  };
};

export const selectUnfallIndicesOfDeactivatedProduktlinien = (state: IAppState): Array<number> => {
  const kus5thSauleFeatureToggle =
    state.transientState.featureToggles[Feature.UNFALL_KUS_5TH_SAEULE];
  const isBasisProdukt = selectSelectedProduktName(state) === Produktlinienname.BASIS;
  const kisZusatzoptionSelected = hasZusatzoptionSelected(
    state,
    UnfFachschluessel.KINDERINVALIDITAET,
  );
  const deactivatedProduktlinien = selectIndicesOfDeactivatedProduktlinien(state);

  if (kus5thSauleFeatureToggle && (isBasisProdukt || kisZusatzoptionSelected)) {
    return [...deactivatedProduktlinien, 4];
  }
  return deactivatedProduktlinien;
};
